'use client';

import { createContext, useState, useContext, useEffect, useMemo, ReactNode } from 'react';
import { useRouter } from 'next/router';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import { useMessageManager } from '@framework/message';
import { getContainer } from '@framework/di/getContainer';
import SubscriptionsRepository from '@/creditflowresearch/infrastructure/repository/SubscriptionsRepository';
import {
  getSubscriptionsArticleParams,
  IArticleParamsConfig,
  getSubscribeParams,
  selectSubscribableRegionIds,
} from '@/creditflowresearch/application/ArticlesAndStats/ArticlesSubscriptions/helpers';
import { usePageFiltersContext } from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import { ARTICLE_SUBSCRIPTION_ID } from '@/creditflowresearch/ui/components/SubscriptionForm/const';
import {
  useUserSubscriptionsContext,
} from '@/creditflowresearch/application/Subscriptions/UserSubscriptionsContext/UserSubscriptionsContext';
import { ThemeType } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/themeTypes';
import { DictionariesContext } from '@/creditflowresearch/application/Dictionaries/DictionariesContext';
import { useHeaderMenuContext } from '@/creditflowresearch/application/HeaderMenu/HeaderMenuContext';
import { useCurrentFilters } from '@/creditflowresearch/application/ArticlesAndStats/ArticlesSubscriptions/useCurrentFilters';
import { usePageSubscriptionState } from '@/creditflowresearch/application/ArticlesAndStats/ArticlesSubscriptions/usePageSubscriptionState';
import { useFilteredGroupedThemes } from '@/creditflowresearch/application/ArticlesAndStats/ArticlesSubscriptions/useFilteredGroupedThemes';
import { useIsGuest } from '@/creditflowresearch/application/User/access';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { UserPlanFeature } from '@creditflowresearch/user-plan/vo/IUserPlanResponse';
import { hasPlanTopicsAccess } from '@/creditflowresearch/application/User/access/hasPlanTopicsAccess';

const useArticlesSubscriptions = (themeType: ThemeType) => {
  const [isFetching, setIsFetching] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const container = getContainer();
  const { getErrorMessage } = useMessageManager();
  const { pathname, asPath, query } = useRouter();
  const { markets, segments } = query;
  const subscriptionsRepository = container.get(SubscriptionsRepository);
  const legacy = useContext(DictionariesContext);
  const { mappedSectors, mappedIndustries } = useHeaderMenuContext();
  const sectorsList = legacy?.sectorsList ?? mappedSectors;
  const industriesList = legacy?.industriesList ?? mappedIndustries;
  const { userSubscriptions, getUserSubscriptions, setCommonButtonSubscribed } = useUserSubscriptionsContext();
  const {
    articleParams: subscriptionArticleParams,
    subscriptionIds,
  } = getSubscriptionsArticleParams(userSubscriptions);
  const isGuest = useIsGuest();

  const {
    pageFilters: {
      productIds,
      regionIds,
      industry,
      sector,
      includedParentId,
      includedCompanyRole,
      isConsumerOverview,
      profileType,
    },
    regionFilters,
  } = usePageFiltersContext();

  const {
    regions: planRegionIds,
    products: planProductIds,
    checkAccessByFeature,
  } = useUserPlanContext();

  const grantsPlanTopics = useMemo(() => hasPlanTopicsAccess({
    hasCommentaryAndDealFlow: checkAccessByFeature(UserPlanFeature.CommentaryAndDealFlow),
    planProductIds,
    productIds,
    markets,
  }), [checkAccessByFeature, markets, planProductIds, productIds]);

  const subscribableRegionIds = useMemo(
    () => selectSubscribableRegionIds(
      regionFilters.filter(({ disabled }) => !disabled).map(({ id }) => id),
      planRegionIds || [],
      grantsPlanTopics,
    ),
    [regionFilters, planRegionIds, grantsPlanTopics],
  );

  const {
    currentRegion,
    currentProduct,
    currentMarket,
    currentSegment,
    isCorporateContext,
  } = useCurrentFilters();

  const {
    isOneFilterSelected,
    isCommonButtonUsed,
    activePage,
  } = usePageSubscriptionState();

  const {
    groupedThemes,
    themesIds,
    themes,
    isThemesFetching,
  } = useFilteredGroupedThemes({
    currentRegion,
    currentProduct,
    currentMarket,
    subscribableRegionIds,
  });

  const isCorporateOrSegmentSelected = Boolean(currentMarket || currentSegment);

  const paramsConfig: IArticleParamsConfig = {
    themes,
    regionIds,
    subscribableRegionIds,
    productIds,
    markets: markets as string,
    segments: segments as string,
    isConsumerOverview,
    themeType,
    sector,
    industry,
    sectorsList,
    industriesList,
    includedParentId,
    includedCompanyRole,
    pathname,
    profileType,
  };

  const onSubscribe = async (articleFormThemes: string[] = []) => {
    const articleParams = getSubscribeParams({
      subscriptionArticleParams,
      paramsConfig,
      articleFormThemes,
      pageType: activePage,
    });

    const payload = {
      subscriptionIds: [...new Set([...subscriptionIds, ARTICLE_SUBSCRIPTION_ID])],
      articleParams,
    };

    try {
      // @ts-ignore TODO: incorrect type of payload with correct repository type
      await subscriptionsRepository.updateUserSubscriptionsBySSID(payload);
      isCommonButtonUsed ? setCommonButtonSubscribed(true) : setIsSubscribed(true);
      getUserSubscriptions(); // if user wants to subscribe again, payload should have existing subscriptions
      AlertManager.success('Your subscription has been successfully updated');
    } catch (e) {
      AlertManager.error(getErrorMessage(e));
    }
  };

  const getPageSubscriptionStatus = async () => {
    setIsFetching(true);

    const articleParams = getSubscribeParams({
      paramsConfig,
      pageType: activePage,
    });

    const payload = {
      subscriptionId: ARTICLE_SUBSCRIPTION_ID,
      articleParams,
    };
    try {
      const response = await subscriptionsRepository.checkUserSubscriptionStatus(payload);
      isCommonButtonUsed ? setCommonButtonSubscribed(response) : setIsSubscribed(response);
    } catch (e) {
      AlertManager.error(getErrorMessage(e));
    } finally {
      setIsFetching(false);
    }
  };

  const hasActiveContext = query.companyViewId || isOneFilterSelected || isCorporateContext;
  const hasThemes = themeType && themes.length;
  const shouldCheckSubscription = hasActiveContext && hasThemes;

  useEffect(() => {
    if (shouldCheckSubscription && !isGuest) {
      getPageSubscriptionStatus();
    }
  }, [shouldCheckSubscription, asPath]);

  return {
    isSubscribed,
    onSubscribe,
    getPageSubscriptionStatus,
    isOneFilterSelected,
    isCorporateOrSegmentSelected,
    isFetching,
    groupedThemes,
    themesIds,
    themes,
    isThemesFetching,
    currentRegion,
    currentProduct,
    currentMarket,
    currentSegment,
    isCorporateContext,
    isCommonButtonUsed,
    subscribableRegionIds,
  };
};

type ArticlesSubscriptionsContextType = ReturnType<typeof useArticlesSubscriptions>;

export const ArticlesSubscriptionsContext = createContext<ArticlesSubscriptionsContextType | null>(null);

export const useArticlesSubscriptionsContext = () => {
  const context = useContext(ArticlesSubscriptionsContext);
  if (!context) {
    throw new Error('useArticlesSubscriptionsContext must be used within the ArticlesSubscriptionsContext');
  }
  return context;
};

interface IContextProps {
  children: ReactNode,
  themeType?: ThemeType,
}

const ArticleSubscriptionsContextProvider = ({ children, themeType }: IContextProps) => (
  <ArticlesSubscriptionsContext.Provider value={ useArticlesSubscriptions(themeType) }>
    { children }
  </ArticlesSubscriptionsContext.Provider>
);

export default ArticleSubscriptionsContextProvider;
